import React, { useState, useEffect } from 'react';
import { Session } from '@supabase/supabase-js';
import { supabase } from '../../supabaseClient';

interface NotificationCenterProps {
  session: Session;
  onNotificationClick?: (resourceType: string, resourceId: string) => void;
}

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  action_url: string | null;
  action_label: string | null;
  priority: string;
  created_at: string;
  resource_type: string | null;
  resource_id: string | null;
}

interface NotificationStats {
  total_count: number;
  unread_count: number;
  urgent_count: number;
  high_priority_count: number;
  today_count: number;
}

export const NotificationCenter: React.FC<NotificationCenterProps> = ({
  session,
  onNotificationClick
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [stats, setStats] = useState<NotificationStats | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch notifications
  const fetchNotifications = async () => {
    const { data: notifData } = await supabase.rpc('get_unread_notifications', {
      p_user_id: session.user.id,
      p_limit: 20
    });

    const { data: statsData } = await supabase.rpc('get_notification_stats', {
      p_user_id: session.user.id
    });

    if (notifData) {
      setNotifications(notifData);
    }

    if (statsData && statsData.length > 0) {
      setStats(statsData[0]);
    }
  };

  useEffect(() => {
    fetchNotifications();

    // Set up realtime subscription
    const notificationsSubscription = supabase
      .channel('notifications_channel')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${session.user.id}`
        },
        () => {
          fetchNotifications();
        }
      )
      .subscribe();

    return () => {
      notificationsSubscription.unsubscribe();
    };
  }, [session.user.id]);

  const handleMarkAsRead = async (notificationId: string) => {
    await supabase.rpc('mark_notifications_read', {
      p_user_id: session.user.id,
      p_notification_ids: [notificationId]
    });

    setNotifications(notifications.filter(n => n.id !== notificationId));
    fetchNotifications();
  };

  const handleMarkAllAsRead = async () => {
    setIsLoading(true);
    await supabase.rpc('mark_notifications_read', {
      p_user_id: session.user.id,
      p_notification_ids: null
    });

    setNotifications([]);
    fetchNotifications();
    setIsLoading(false);
  };

  const handleNotificationClick = (notification: Notification) => {
    handleMarkAsRead(notification.id);
    
    if (onNotificationClick && notification.resource_type && notification.resource_id) {
      onNotificationClick(notification.resource_type, notification.resource_id);
    }
    
    setIsOpen(false);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'deadline': return 'schedule';
      case 'success': return 'check_circle';
      case 'warning': return 'warning';
      case 'error': return 'error';
      case 'mention': return 'alternate_email';
      case 'share': return 'share';
      case 'system': return 'settings';
      default: return 'notifications';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'deadline': return 'text-orange-600 dark:text-orange-400';
      case 'success': return 'text-green-600 dark:text-green-400';
      case 'warning': return 'text-yellow-600 dark:text-yellow-400';
      case 'error': return 'text-red-600 dark:text-red-400';
      case 'mention': return 'text-blue-600 dark:text-blue-400';
      case 'share': return 'text-purple-600 dark:text-purple-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getPriorityBadge = (priority: string) => {
    if (priority === 'urgent') {
      return (
        <span className="px-2 py-0.5 text-xs bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 rounded-full">
          Dringend
        </span>
      );
    }
    if (priority === 'high') {
      return (
        <span className="px-2 py-0.5 text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 rounded-full">
          Hoch
        </span>
      );
    }
    return null;
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return 'Gerade eben';
    if (diffMins < 60) return `vor ${diffMins} Min.`;
    if (diffHours < 24) return `vor ${diffHours} Std.`;
    
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="relative">
      {/* Notification Bell */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-full hover:bg-white/20 dark:hover:bg-gray-700 transition-colors"
        title="Benachrichtigungen"
      >
        <span className="material-symbols-outlined text-white">
          notifications
        </span>
        
        {stats && stats.unread_count > 0 && (
          <span className="absolute -top-1 -right-1 px-2 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full min-w-[20px] text-center">
            {stats.unread_count > 99 ? '99+' : stats.unread_count}
          </span>
        )}
      </button>

      {/* Notification Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown Panel */}
          <div className="absolute right-0 mt-2 w-96 bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 z-50 max-h-[600px] flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Benachrichtigungen
                </h3>
                {notifications.length > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    disabled={isLoading}
                    className="text-sm text-luna-secondary hover:text-luna-primary disabled:opacity-50"
                  >
                    Alle gelesen
                  </button>
                )}
              </div>

              {stats && (
                <div className="flex items-center gap-3 text-xs text-gray-600 dark:text-gray-400">
                  <span>{stats.unread_count} ungelesen</span>
                  {stats.urgent_count > 0 && (
                    <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 rounded-full">
                      {stats.urgent_count} dringend
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Notifications List */}
            <div className="flex-1 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                  <span className="material-symbols-outlined text-6xl text-gray-300 dark:text-gray-600 mb-4">
                    notifications_off
                  </span>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">
                    Keine neuen Benachrichtigungen
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        {/* Icon */}
                        <span className={`material-symbols-outlined text-2xl ${getTypeColor(notification.type)}`}>
                          {getTypeIcon(notification.type)}
                        </span>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h4 className="font-semibold text-sm text-gray-900 dark:text-white">
                              {notification.title}
                            </h4>
                            {getPriorityBadge(notification.priority)}
                          </div>

                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            {notification.message}
                          </p>

                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {formatDate(notification.created_at)}
                            </span>

                            {notification.action_label && (
                              <span className="text-xs text-luna-secondary font-medium">
                                {notification.action_label} →
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Mark as read button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkAsRead(notification.id);
                          }}
                          className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                          title="Als gelesen markieren"
                        >
                          <span className="material-symbols-outlined text-sm">check</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="p-3 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => {
                    setIsOpen(false);
                    // Navigate to all notifications page
                  }}
                  className="w-full text-sm text-center text-luna-secondary hover:text-luna-primary transition-colors"
                >
                  Alle Benachrichtigungen anzeigen
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
